import { TypographyBody, FlexGrid, SelectField, usePrevious, SwitchControlLabel, TextField, DateField, PasswordField, useForkRef, Styler, Stacker, MultilineField, } from '@honeycomb/ui-core';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getValidationStatus } from '../../utils';
import { TokenisingField } from '../booking/TokenisingField';
import { useValidatorContext } from './ValidatorContext';
import { ValidatorDispatchActionType } from './ValidatorReducer';
export var ValidationEnum;
(function (ValidationEnum) {
    ValidationEnum["CHANGE"] = "change";
    ValidationEnum["BLUR"] = "blur";
    ValidationEnum["REFRESH"] = "refresh";
})(ValidationEnum || (ValidationEnum = {}));
export var FieldType;
(function (FieldType) {
    FieldType["INPUT"] = "input";
    FieldType["SELECT"] = "select";
    FieldType["SWITCH"] = "switch";
    FieldType["TOKENISING"] = "tokenising";
    FieldType["UNSUPPORTED"] = "unsupported";
})(FieldType || (FieldType = {}));
const getValueFromField = (fieldType, element) => {
    var _a;
    if (fieldType === FieldType.INPUT) {
        return element === null || element === void 0 ? void 0 : element.value;
    }
    if (fieldType === FieldType.SELECT) {
        return (_a = element.options) === null || _a === void 0 ? void 0 : _a[element.selectedIndex].value;
    }
    if (fieldType === FieldType.SWITCH) {
        return String(element === null || element === void 0 ? void 0 : element.checked);
    }
    return '';
};
export const ValidatorField = React.forwardRef(function ValidatorField({ GridItemProps, children, fieldRef, id, initialValue, invalidText = () => '', onValidBlur, onValidChange, showMessage = false, label, validate, validateOnBlur = false, validateOnChange = false, isHighLighted = false, validText, labelProps, }, ref) {
    const [valid, setValid] = useState(null);
    const [message, setMessage] = useState('Error!');
    const childRef = useRef(null);
    const handleRef = useForkRef(fieldRef, childRef);
    const child = React.Children.only(children);
    let fieldType;
    switch (React.isValidElement(child) && child.type) {
        case TextField:
        case PasswordField:
        case MultilineField:
        case DateField:
            fieldType = FieldType.INPUT;
            break;
        case SelectField:
            fieldType = FieldType.SELECT;
            break;
        case SwitchControlLabel:
            fieldType = FieldType.SWITCH;
            break;
        case TokenisingField:
            fieldType = FieldType.TOKENISING;
            break;
        default:
            fieldType = FieldType.UNSUPPORTED;
            break;
    }
    // If the child field is a "SwitchControlLabel" then we need to target the child's switch control rather than the child itself.
    const childControl = child.type === SwitchControlLabel
        ? child.props.control
        : child;
    // If present, extract existing onChange and onBlur event handlers from the child control.
    // These are not present on "TokenisingField".
    let existingOnChange = null;
    let existingOnBlur = null;
    if (childControl && 'props' in childControl) {
        const childProps = childControl.props;
        if ('onChange' in childProps) {
            existingOnChange = childProps.onChange || null;
        }
        if ('onBlur' in childProps) {
            existingOnBlur = childProps.onBlur || null;
        }
    }
    const { validatorState: { refreshIds, invalidMessages }, validatorDispatch, } = useValidatorContext();
    const refreshId = refreshIds[id];
    const prevRefreshId = usePrevious(refreshId);
    // Validate initial value
    useEffect(() => {
        var _a, _b;
        if (initialValue && childRef.current && fieldType === FieldType.INPUT) {
            // add condition here because dateField control
            childRef.current.value = !childRef.current.value ? initialValue : childRef.current.value;
            if (validate(childRef.current.value)) {
                setValid(true);
            }
        }
        if (fieldType === FieldType.SELECT &&
            validate((_a = childRef.current.options) === null || _a === void 0 ? void 0 : _a[childRef.current.selectedIndex].value)) {
            setValid(true);
        }
        if (childRef.current && !((_b = childRef.current) === null || _b === void 0 ? void 0 : _b.value) && validate()) {
            setValid(true);
        }
        // fixed for HCW-3084
        if (initialValue && validate(initialValue) && invalidMessages[id]) {
            validatorDispatch({
                type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                fieldId: id,
                invalidMessage: null,
            });
        }
    }, [initialValue, fieldType, validate, invalidMessages, id, validatorDispatch]);
    // Run a validation check when the refresh value from the context is changed
    useEffect(() => {
        if (refreshId && prevRefreshId !== refreshId) {
            const value = getValueFromField(fieldType, childRef.current || undefined);
            const newValid = validate(value, valid, ValidationEnum.REFRESH);
            if (newValid === false) {
                setMessage(invalidText(value));
                validatorDispatch({
                    type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                    fieldId: id,
                    invalidMessage: invalidText(value),
                });
            }
            else {
                if (!showMessage)
                    validatorDispatch({
                        type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                        fieldId: id,
                        invalidMessage: null,
                    });
                if ((newValid === true || showMessage) && validText) {
                    setMessage(validText(value));
                }
            }
            validatorDispatch({ type: ValidatorDispatchActionType.CLEAR_REFRESH_ID, fieldId: id });
            setValid(newValid);
        }
    }, [
        childRef,
        id,
        invalidText,
        fieldType,
        prevRefreshId,
        refreshId,
        valid,
        validText,
        validate,
        validatorDispatch,
        showMessage,
    ]);
    const handleBlur = useCallback((event) => {
        var _a;
        existingOnBlur === null || existingOnBlur === void 0 ? void 0 : existingOnBlur(event);
        if (childRef.current) {
            childRef.current.value = (_a = childRef.current) === null || _a === void 0 ? void 0 : _a.value.trim();
        }
        const value = getValueFromField(fieldType, childRef.current || undefined);
        let newValid = valid;
        if (validateOnBlur) {
            newValid = validate(value, valid, ValidationEnum.BLUR);
            if (newValid === false) {
                setMessage(invalidText(value));
                validatorDispatch({
                    type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                    fieldId: id,
                    invalidMessage: invalidText(value),
                });
            }
            else {
                validatorDispatch({
                    type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                    fieldId: id,
                    invalidMessage: null,
                });
                if (newValid === true && validText) {
                    setMessage(validText(value));
                }
            }
            setValid(newValid);
        }
        if (newValid !== false && onValidBlur) {
            onValidBlur(value, event);
        }
    }, [
        existingOnBlur,
        fieldType,
        id,
        invalidText,
        onValidBlur,
        valid,
        validText,
        validate,
        validateOnBlur,
        validatorDispatch,
    ]);
    const handleChange = useCallback((event) => {
        var _a;
        const { inputType } = (_a = event.nativeEvent) !== null && _a !== void 0 ? _a : '';
        const backspaceKeyEvent = inputType === 'deleteContentBackward';
        existingOnChange === null || existingOnChange === void 0 ? void 0 : existingOnChange(event);
        const value = getValueFromField(fieldType, childRef.current || undefined);
        let newValid = valid;
        if (validateOnChange && !backspaceKeyEvent) {
            newValid = validate(value, valid, ValidationEnum.CHANGE);
            if (newValid === false) {
                setMessage(invalidText(value));
                validatorDispatch({
                    type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                    fieldId: id,
                    invalidMessage: invalidText(value),
                });
            }
            else {
                validatorDispatch({
                    type: ValidatorDispatchActionType.SET_INVALID_MESSAGE,
                    fieldId: id,
                    invalidMessage: null,
                });
                if (newValid === true && validText) {
                    setMessage(validText(value));
                }
            }
            setValid(newValid);
        }
        if (newValid !== false && onValidChange) {
            onValidChange(value, event);
        }
    }, [
        existingOnChange,
        id,
        invalidText,
        onValidChange,
        valid,
        validText,
        validate,
        validateOnChange,
        validatorDispatch,
        fieldType,
    ]);
    const validationStatus = getValidationStatus({
        error: valid === false,
        success: valid === true,
        highlight: isHighLighted,
    });
    const getControl = React.useCallback(() => {
        switch (fieldType) {
            case FieldType.INPUT:
                return React.cloneElement(childControl, {
                    inputRef: handleRef,
                    validationStatus,
                    id,
                    onBlur: handleBlur,
                    onChange: handleChange,
                });
            case FieldType.SELECT:
                return React.cloneElement(childControl, {
                    selectRef: handleRef,
                    validationStatus,
                    id,
                    onBlur: handleBlur,
                    onChange: handleChange,
                });
            case FieldType.SWITCH:
                return React.cloneElement(child, {
                    control: React.cloneElement(childControl, {
                        inputRef: handleRef,
                        error: validationStatus === 'error',
                        id,
                        onBlur: handleBlur,
                        onChange: handleChange,
                    }),
                });
            case FieldType.TOKENISING:
                return React.cloneElement(childControl, {
                    validationStatus, // TokenisingField field only needs validationStatus
                });
            case FieldType.UNSUPPORTED:
            default:
                return null;
        }
    }, [child, childControl, fieldType, handleBlur, handleChange, handleRef, id, validationStatus]);
    return (React.createElement(Styler, { ref: ref },
        React.createElement(Stacker, { spacing: fieldType === FieldType.SWITCH ? 2 : 1 },
            label && (React.createElement(TypographyBody, Object.assign({ component: "label", htmlFor: id, weight: "semiBold" }, labelProps), label)),
            React.createElement(FlexGrid, { container: true },
                React.createElement(FlexGrid, Object.assign({ xs: "fill" }, GridItemProps), getControl())),
            valid === false && message && (React.createElement(TypographyBody, { component: "div", size: "xs", color: isHighLighted ? 'system.info.base' : 'system.error.base', weight: "semiBold" }, message)),
            (valid === true || showMessage) && validText && message && (React.createElement(TypographyBody, { component: "div", size: "xs", color: "system.success.base" }, message)))));
});
